import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Box,
  Typography,
  Chip
} from '@mui/material';
import { AccountBalance as AccountBalanceIcon } from '@mui/icons-material';
import { CaixaFinanceiro, caixaFinanceiroService } from '../../services/caixaFinanceiroService';

interface CaixaFinanceiroSelectProps {
  value: number | null;
  onChange: (caixaId: number | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  label?: string;
  required?: boolean;
  escritorioId?: number; // Filtrar caixas por escritório específico
}

const CaixaFinanceiroSelect: React.FC<CaixaFinanceiroSelectProps> = ({
  value,
  onChange,
  error = false,
  helperText,
  disabled = false,
  size = 'small',
  fullWidth = true,
  label = 'Caixa Financeiro',
  required = false,
  escritorioId
}) => {
  const [caixas, setCaixas] = useState<CaixaFinanceiro[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Carregar caixas financeiros ativos
  useEffect(() => {
    const loadCaixas = async () => {
      setLoading(true);
      setErrorMessage('');
      
      try {
        const filters: any = {
          ativo: 'true' as const,
          status: 'aberto' as const,
          limit: 1000 // Buscar todos os caixas ativos
        };

        // Se especificado um escritório, filtrar por ele
        if (escritorioId) {
          filters.escritorio_id = escritorioId;
        }

        const response = await caixaFinanceiroService.getCaixas(filters);
        
        if (response.success && response.data) {
          setCaixas(response.data);
        } else {
          setErrorMessage('Erro ao carregar caixas financeiros');
        }
      } catch (error) {
        console.error('Erro ao carregar caixas financeiros:', error);
        setErrorMessage('Erro ao carregar caixas financeiros');
      } finally {
        setLoading(false);
      }
    };

    loadCaixas();
  }, [escritorioId]);

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    onChange(selectedValue === '' ? null : selectedValue);
  };

  const getCaixaDisplayName = (caixa: CaixaFinanceiro): string => {
    let displayName = caixa.nome;
    
    // Adicionar informações do vínculo
    if (caixa.tipo_vinculo === 'escritorio' && caixa.escritorio) {
      displayName += ` (${caixa.escritorio.nome_fantasia})`;
    } else if (caixa.tipo_vinculo === 'usuario' && caixa.usuario) {
      displayName += ` (${caixa.usuario.nome})`;
    }
    
    // Adicionar indicador de caixa padrão
    if (caixa.is_default) {
      displayName += ' - Padrão';
    }
    
    return displayName;
  };

  const getCaixaStatusColor = (caixa: CaixaFinanceiro): 'default' | 'primary' | 'secondary' => {
    if (caixa.is_default) return 'primary';
    if (caixa.saldo_atual < 0) return 'secondary';
    return 'default';
  };

  const formatSaldo = (saldo: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(saldo);
  };

  if (loading) {
    return (
      <FormControl fullWidth={fullWidth} size={size} error={error} required={required}>
        <InputLabel>{label}</InputLabel>
        <Box display="flex" alignItems="center" justifyContent="center" p={2}>
          <CircularProgress size={20} />
          <Typography variant="body2" sx={{ ml: 1 }}>
            Carregando caixas...
          </Typography>
        </Box>
      </FormControl>
    );
  }

  if (errorMessage) {
    return (
      <FormControl fullWidth={fullWidth} size={size} error={true} required={required}>
        <InputLabel>{label}</InputLabel>
        <Box display="flex" alignItems="center" p={2}>
          <Typography variant="body2" color="error">
            {errorMessage}
          </Typography>
        </Box>
      </FormControl>
    );
  }

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      error={error} 
      required={required}
      disabled={disabled}
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={value || ''}
        label={label}
        onChange={handleChange}
        startAdornment={<AccountBalanceIcon sx={{ mr: 1, color: 'text.secondary' }} />}
        renderValue={(selected) => {
          if (!selected) return '';
          const caixa = caixas.find(c => c.id === selected);
          if (!caixa) return '';
          
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2">
                {caixa.nome}
              </Typography>
              <Chip 
                label={formatSaldo(caixa.saldo_atual)}
                size="small"
                color={getCaixaStatusColor(caixa)}
                variant="outlined"
              />
              {caixa.is_default && (
                <Chip 
                  label="Padrão"
                  size="small"
                  color="primary"
                  variant="filled"
                />
              )}
            </Box>
          );
        }}
      >
        {caixas.length === 0 ? (
          <MenuItem disabled>
            <Typography variant="body2" color="text.secondary">
              Nenhum caixa financeiro ativo encontrado
            </Typography>
          </MenuItem>
        ) : (
          caixas.map((caixa) => (
            <MenuItem key={caixa.id} value={caixa.id}>
              <Box display="flex" flexDirection="column" width="100%">
                <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                  <Typography variant="body2" fontWeight={caixa.is_default ? 600 : 400}>
                    {getCaixaDisplayName(caixa)}
                  </Typography>
                  <Chip 
                    label={formatSaldo(caixa.saldo_atual)}
                    size="small"
                    color={getCaixaStatusColor(caixa)}
                    variant="outlined"
                  />
                </Box>
                <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                  <Typography variant="caption" color="text.secondary">
                    Status: {caixa.status === 'aberto' ? 'Aberto' : 'Fechado'}
                  </Typography>
                  {caixa.tipo_vinculo === 'escritorio' && caixa.escritorio && (
                    <Typography variant="caption" color="text.secondary">
                      • {caixa.escritorio.nome_fantasia}
                    </Typography>
                  )}
                  {caixa.tipo_vinculo === 'usuario' && caixa.usuario && (
                    <Typography variant="caption" color="text.secondary">
                      • {caixa.usuario.nome}
                    </Typography>
                  )}
                </Box>
              </Box>
            </MenuItem>
          ))
        )}
      </Select>
      {helperText && (
        <Typography variant="caption" color={error ? 'error' : 'text.secondary'} sx={{ mt: 0.5, ml: 1.75 }}>
          {helperText}
        </Typography>
      )}
    </FormControl>
  );
};

export default CaixaFinanceiroSelect;
